import React, { useState } from "react";
import { Box, Image, Skeleton, Text } from "@chakra-ui/react";
import { useBrandingContext } from "@root/context/branding-context";
import { getOrganizationDisplayName } from "@root/utils/branding";

interface BrandedLogoProps {
  variant?: "full" | "small";
  width?: number | string;
  height?: number | string;
  fallbackText?: string;
}

export const BrandedLogo: React.FC<BrandedLogoProps> = ({
  variant = "full",
  width,
  height,
  fallbackText,
}) => {
  const { logoUrl, organizationName, organizationBranding, isLoading } = useBrandingContext();
  const [imgError, setImgError] = useState(false);

  const defaultWidth = variant === "small" ? "100px" : "100px";
  const defaultHeight = variant === "small" ? "50px" : "50px";
  const resolvedWidth = width || defaultWidth;
  const resolvedHeight = height || defaultHeight;

  if (isLoading) {
    return (
      <Skeleton
        width={`${resolvedWidth}px`}
        height={`${resolvedHeight}px`}
        borderRadius="4px"
      />
    );
  }

  // If we have a logo URL and it hasn't errored, show it
  if (logoUrl && !imgError) {
    return (
      <Image
        src={logoUrl}
        alt={organizationName || "Logo"}
        width={resolvedWidth}
        height={resolvedHeight}
        objectFit="contain"
        onError={() => setImgError(true)}
      />
    );
  }

  // Fallback to organization name/subdomain text
  const displayName = fallbackText || getOrganizationDisplayName(
    organizationName,
    organizationBranding?.domain
  );
  
  return (
    <Text
      fontWeight="bold"
      fontSize={variant === "small" ? "lg" : "xl"}
      color="#15161A"
      letterSpacing="0.5px"
    >
      {displayName}
    </Text>
  );
};
